import { Section } from "@apex/component-library";
import {
  Box,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { useGetBalanceRollupDetails } from "../../api/useGetBalanceRollupDetails";
import { BalancesResponse, useGetBalances } from "../../api/useGetBalances";
import { MarginsResponse, useGetMargins } from "../../api/useGetMargins";
import { useBalanceRollupStore } from "../../hooks/useBalanceRollupStore";
import { balancesEnumToLabel } from "../../utils/balancesEnumToLabel";
import BalanceRollupTable from "../MarginAccountDetailsTabs/BuyingPowerTab/BalanceRollupTable";
import ToggleSwitchRolledUp from "../ToggleSwitchRolledup";
import {
  Data,
  formatPercent,
  getCurrencyValue,
  getDataSection,
} from "./auxUtils";
import { SectionLoadingState } from "./RenderState";

export interface BuyingPowerProps {
  accountId: string;
}

const BuyingPower = ({ accountId }: BuyingPowerProps) => {
  const [isShowingRollup, isMasterSubAccount] = useBalanceRollupStore(state => [
    state.isShowingRollup,
    state.isMasterSubAccount,
  ]);

  const { data: balancesData, isLoading: balancesLoading } =
    useGetBalances(accountId);

  const {
    data: marginData,
    isLoading: isMarginsLoading,
    error: marginsError,
  } = useGetMargins(accountId);

  const { data: rollupData } = useGetBalanceRollupDetails({
    accountNumber: accountId,
  });

  const buildBuyingPower = (marginData: MarginsResponse): Data[] => {
    const {
      marginExcess,
      cashExcessEquity,
      marginConcentrationExcess,
      // marginExcessEquityNYSE removed (FEECM-17670 Req 9)
      totalConcentrationExcess,
      unsettledCash,
      overnightBuyingPower,
      dayTradeBuyingPower,
      cashAvailableToWithdraw,
      sma,
      committedSma,
      marginEquityPercent,
    } = marginData;
    return [
      {
        label: "Margin Excess",
        value: getCurrencyValue(marginExcess),
        isNumeric: true,
      },
      {
        label: "Cash Excess Equity",
        value: getCurrencyValue(cashExcessEquity),
        isNumeric: true,
      },
      {
        label: "Margin Concentrated Excess",
        value: getCurrencyValue(marginConcentrationExcess),
        isNumeric: true,
      },
      // Margin Excess Equity NYSE field removed (FEECM-17670 Req 9)
      {
        label: "Total Concentrated Excess",
        value: getCurrencyValue(totalConcentrationExcess),
        isNumeric: true,
      },
      {
        label: "Unsettled Cash",
        value: getCurrencyValue(unsettledCash),
        isNumeric: true,
      },
      {
        label: "Overnight Buying Power",
        value: getCurrencyValue(overnightBuyingPower),
        isNumeric: true,
      },
      {
        label: "IMBP", // Updated abbreviation (FEECM-17670 Req 8)
        value: getCurrencyValue(dayTradeBuyingPower),
        isNumeric: true,
      },
      {
        label: "Available to Withdraw",
        value: getCurrencyValue(cashAvailableToWithdraw),
        isNumeric: true,
      },
      {
        label: "SMA",
        value: getCurrencyValue(sma),
        isNumeric: true,
      },
      {
        label: "Committed SMA",
        value: getCurrencyValue(committedSma),
        isNumeric: true,
      },
      {
        label: "Margin Equity Percent",
        value: formatPercent(marginEquityPercent),
        isNumeric: true,
      },
    ];
  };

  const buildEquity = (marginData: MarginsResponse) => {
    const {
      cashMarketValue,
      marginMarketValue,
      shortMarketValue,
      marginEquity,
      netLiquidity,
    } = marginData;
    return [
      {
        label: "Cash Market Value",
        value: getCurrencyValue(cashMarketValue),
        isNumeric: true,
      },
      {
        label: "Margin Market Value",
        value: getCurrencyValue(marginMarketValue),
        isNumeric: true,
      },
      {
        label: "Short Market Value",
        value: getCurrencyValue(shortMarketValue),
        isNumeric: true,
      },
      {
        label: "Margin Equity",
        value: getCurrencyValue(marginEquity),
        isNumeric: true,
      },
      {
        label: "Net Liquidating Value",
        value: getCurrencyValue(netLiquidity),
        isNumeric: true,
      },
    ];
  };
  const buildRequirements = (marginData: MarginsResponse) => {
    const {
      nonOptionsRequirementNotConcentrated,
      nonOptionsRequirement,
      optionsRequirement,
      marginRequirement,
      marginConcentrationExcess,
      marginRequirementNYSE,
    } = marginData;
    return [
      {
        label: "Non-Options Requirement",
        value: getCurrencyValue(nonOptionsRequirementNotConcentrated),
        isNumeric: true,
      },
      {
        label: "Non-Options Requirement with Concentration",
        value: getCurrencyValue(nonOptionsRequirement),
        isNumeric: true,
      },
      {
        label: "Options Requirement",
        value: getCurrencyValue(optionsRequirement),
        isNumeric: true,
      },
      {
        label: "Total Requirement",
        value: getCurrencyValue(marginRequirement),
        isNumeric: true,
      },
      {
        label: "Total Requirement with Concentration",
        value: getCurrencyValue(marginConcentrationExcess),
        isNumeric: true,
      },
      {
        label: "Margin Requirement NYSE",
        value: getCurrencyValue(marginRequirementNYSE),
        isNumeric: true,
      },
    ];
  };

  const tradeTotal = balancesData
    ? Object.values(balancesData)?.reduce(
        (acc, curr) =>
          acc + curr?.reduce((acc, curr) => acc + curr.tradeBalance, 0),
        0
      )
    : 0;

  const settledTotal = balancesData
    ? Object.values(balancesData)?.reduce(
        (acc, curr) =>
          acc + curr?.reduce((acc, curr) => acc + curr.settleBalance, 0),
        0
      )
    : 0;

  return (
    <Box>
      {getDataSection({
        title: "Buying Power",
        data: marginData ? buildBuyingPower(marginData) : [],
        isLoading: isMarginsLoading,
        error: marginsError,
        skeletonLines: 12,
      })}
      {getDataSection({
        title: "Equity",
        data: marginData ? buildEquity(marginData) : [],
        isLoading: isMarginsLoading,
        error: marginsError,
        skeletonLines: 5,
      })}
      {getDataSection({
        title: "Requirements",
        data: marginData ? buildRequirements(marginData) : [],
        isLoading: isMarginsLoading,
        error: marginsError,
        skeletonLines: 6,
      })}

      {balancesLoading && <SectionLoadingState />}

      {!balancesLoading && balancesData && (
        <Section pt={4} w="100%">
          <Stack direction="row" w="100%" justify="space-between">
            <Text as="h4" textStyle="h5" pb={2}>
              Balances
            </Text>
            <ToggleSwitchRolledUp />
          </Stack>
          <Box>
            <TableContainer mb="5">
              <Table
                variant="simple"
                size="sm"
                marginTop="1"
                data-testid="balances-table"
              >
                {isShowingRollup && !!rollupData && isMasterSubAccount && (
                  <BalanceRollupTable rollupData={rollupData} />
                )}
                {!isShowingRollup && (
                  <BalancesTableContents
                    tradeTotal={tradeTotal}
                    settledTotal={settledTotal}
                    balancesData={balancesData}
                  />
                )}
              </Table>
            </TableContainer>
          </Box>
        </Section>
      )}
    </Box>
  );
};

export default BuyingPower;

type BalancesTableContentsProps = {
  tradeTotal: number;
  settledTotal: number;
  balancesData?: BalancesResponse;
};

function BalancesTableContents({
  tradeTotal,
  settledTotal,
  balancesData,
}: BalancesTableContentsProps) {
  return (
    <>
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th isNumeric>Trade</Th>
          <Th isNumeric>Settled</Th>
        </Tr>
      </Thead>
      <Tbody>
        {balancesData &&
          Object.entries(balancesData).map(([name, b]) => {
            return b.map((bal, i) => {
              return (
                <Tr>
                  <Td>{balancesEnumToLabel(name)}</Td>
                  <Td
                    isNumeric
                    data-testid={`balance-${name}-trade-${
                      i + 1
                    }`.toLocaleLowerCase()}
                  >
                    {getCurrencyValue(bal.tradeBalance)}
                  </Td>
                  <Td
                    isNumeric
                    data-testid={`balance-${name}-settled-${
                      i + 1
                    }`.toLocaleLowerCase()}
                  >
                    {getCurrencyValue(bal.settleBalance)}
                  </Td>
                </Tr>
              );
            });
          })}

        <Tr>
          <Td fontWeight="bold" borderBottom="0">
            Totals
          </Td>
          <Td
            isNumeric
            data-testid="balance-total-trade"
            fontWeight="bold"
            borderBottom="0"
          >
            {getCurrencyValue(tradeTotal, 2)}
          </Td>
          <Td
            isNumeric
            data-testid="balance-total-settled"
            fontWeight="bold"
            borderBottom="0"
          >
            {getCurrencyValue(settledTotal, 2)}
          </Td>
        </Tr>
      </Tbody>
    </>
  );
}
